import React, { useState, FormEvent } from "react";
import ButtonLoader from "../../../../UI/Loaders/Loaders";
import {
  handleSwalSuccessAlert,
  handleSwalErrorAlert,
} from "../../../../helpers";
import { errorHandler } from "../../../../helpers/errorHandler";
import { postData } from "../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";

interface ISendInvestmentCertificateModalProps {
  dataRecord: {
    investmentId: number;
    currency: string;
    customerEmail?: string;
  };
  onCloseModal: () => void;
}

const SendInvestmentCertificateModal = ({
  dataRecord,
  onCloseModal,
}: ISendInvestmentCertificateModalProps) => {
  const [formData, setFormData] = useState({
    sendToThirdParty: false,
    destinationEmail: dataRecord.customerEmail || "",
  });
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      sendToThirdParty: checked,
      destinationEmail: checked ? "" : dataRecord.customerEmail || "",
    }));
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.destinationEmail) {
      setError("Please provide a destination email");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.destinationEmail)) {
      setError("Please provide a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const requestBody = {
        investmentId: dataRecord.investmentId,
        email: formData.destinationEmail,
      };

      const response = await postData(
        ajaxEndpoints.SEND_INVESTMENT_CERTIFICATE,
        requestBody,
      );

      handleSwalSuccessAlert(
        "Success",
        response?.data?.message || "Investment certificate sent successfully",
      );
      onCloseModal();
    } catch (error) {
      const errorMessage = errorHandler(error);
      setError(errorMessage);
      handleSwalErrorAlert(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="Modal__dialog md-modal animated slideInDown"
      role="document"
    >
      <div className="Modal__content" style={{ overflow: "hidden" }}>
        <div className="Modal__body">
          <React.Fragment>
            <h3 className="Modal__title p-b-1">Send Investment Certificate</h3>
            <p className="text-muted mb-4">
              Select the recipient email to send the investment certificate
            </p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <div className="">
                    <label htmlFor="sendToThirdParty" className="mt-2">
                      Send to third party email address
                    </label>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="sendToThirdParty"
                        name="sendToThirdParty"
                        checked={formData.sendToThirdParty}
                        onChange={handleSwitchChange}
                        disabled={isSending}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="sendToThirdParty"
                      ></label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="label-txt form-label">
                    Recipient Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control user__form"
                    name="destinationEmail"
                    value={formData.destinationEmail}
                    onChange={handleInputChange}
                    placeholder={
                      formData.sendToThirdParty
                        ? "Enter third party email address"
                        : dataRecord.customerEmail
                          ? "Investor email"
                          : "Enter investor email address"
                    }
                    disabled={
                      isSending ||
                      (!formData.sendToThirdParty &&
                        Boolean(dataRecord.customerEmail))
                    }
                    required
                  />
                  <small className="form-text text-muted">
                    {formData.sendToThirdParty
                      ? "Enter the email address where the certificate should be sent"
                      : dataRecord.customerEmail
                        ? "Certificate will be sent to investor's registered email"
                        : "Enter the investor's email address to send the certificate"}
                  </small>
                </div>
              </div>

              <div className="Form__action-buttons m-t-2">
                <button
                  type="button"
                  onClick={onCloseModal}
                  className="btn zoom-element btn login-btn m-0 mr-auto"
                  disabled={isSending}
                >
                  <i className="fa fa-times-circle" /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn zoom-element btn login-btn m-0 ml-auto"
                  disabled={isSending}
                >
                  {isSending ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      <i className="fa fa-paper-plane" /> Send Certificate
                    </>
                  )}
                </button>
              </div>
            </form>
          </React.Fragment>
        </div>
      </div>
    </div>
  );
};

export default SendInvestmentCertificateModal;
